import axios, { AxiosError } from "axios";
import axiosInstance from "config/axiosConfig";
import {
    Group,
    CreateGroupRequest,
    AddMemberRequest,
    UpdateMemberStatusRequest,
    UpdateCoordinatorRequest,
} from "../types";

export const API_PATHS = {
    GROUPS: "/groups",
    GROUPS_ALL: "/groups/all",
    GROUPS_MEMBERS: "/groups/members",
    GROUPS_MEMBERS_STATUS: "/groups/members/status",
    GROUPS_COORDINATOR: "/groups/coordinator",
};

export type ErrorType = {
    error: string;
    message: string;
    status: number;
    timestamp: string;
};

const handleError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorType>;
        return axiosError.response?.data?.message || "An unknown error occurred";
    }
    return "An unknown error occurred";
};

export const createGroup = async (data: CreateGroupRequest): Promise<Group> => {
    try {
        const response = await axiosInstance.post(API_PATHS.GROUPS, data);
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const getAllGroups = async (): Promise<Group[]> => {
    try {
        const response = await axiosInstance.get(API_PATHS.GROUPS_ALL);
        // Handle both array and object response formats
        if (response.data && typeof response.data === 'object' && 'groups' in response.data) {
            return response.data.groups;
        }
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const getGroup = async (id: string, city: string): Promise<Group> => {
    try {
        const response = await axiosInstance.get(API_PATHS.GROUPS, {
            params: { id, city },
        });
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const addMember = async (data: AddMemberRequest): Promise<Group> => {
    try {
        const response = await axiosInstance.post(API_PATHS.GROUPS_MEMBERS, data);
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const removeMember = async (
    id: string,
    phoneNumber: string,
    city: string
): Promise<void> => {
    try {
        await axiosInstance.delete(API_PATHS.GROUPS_MEMBERS, {
            params: { id, phoneNumber, city },
        });
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const updateMemberStatus = async (
    data: UpdateMemberStatusRequest
): Promise<Group> => {
    try {
        const response = await axiosInstance.put(
            API_PATHS.GROUPS_MEMBERS_STATUS,
            data
        );
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const updateCoordinator = async (
    data: UpdateCoordinatorRequest
): Promise<Group> => {
    try {
        const response = await axiosInstance.put(
            API_PATHS.GROUPS_COORDINATOR,
            data
        );
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};
